/* eslint-disable max-lines-per-function */
import { useCallback, useMemo } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useIOSAmounts, useYearlyMembershipExperimentAmounts } from 'src/app/shared/hooks/membership';

import {
	useMembershipAwarenessFormData,
	useMembershipAwarenessFormFcm,
	useMembershipAwarenessFormFcmExperiments,
	useMembershipAwarenessFormState,
} from '../../../../formContext';
import type { Props } from '../../../../shared/types';
import { getSubscriptionAmount } from '../../../../shared/utils/getSubscriptionAmount';

type Result = ModelHookResult<
	{
		buttonAmounts: ReadonlyArray<{ amount: number; amountLocalized: string }>;
		selectedAmount: { amount: number; amountLocalized: string };
		buttonText?: string;
		displayPayWithText: boolean;
	},
	{
		onClick: (amount?: number) => void;
	}
>;

export function useAmountSelector({ sourceLocation, slug }: Props): Result {
	const track = useTracking();
	const navigate = useNavigate();
	const { amountToBaseUnits, translate } = useI18n();
	const {
		subscriptionsAmounts: buttonAmounts,
		subscriptionsDefaultAmount: defaultAmount,
		currency,
	} = useMembershipAwarenessFormData();
	const { subscriptionConfig, iosAmountsEnabled, yearlyContributionsAmounts } = useMembershipAwarenessFormFcm();
	const utilityContext = useUtilityContext();
	const { defaultAmountExperiment, showYearlyContributions } = useMembershipAwarenessFormFcmExperiments();
	const [{ paymentTerm }] = useMembershipAwarenessFormState();

	const iosAmounts = useIOSAmounts();
	const yearlyContributionsExperimentAmounts = useYearlyMembershipExperimentAmounts();

	const iosAmountExperimentResult: ReturnType<typeof iosAmounts> = useMemo(() => {
		if (iosAmountsEnabled) {
			return iosAmounts();
		}
		return { enabled: false };
	}, [iosAmounts, iosAmountsEnabled]);

	const yearlyContributionsExperimentAmountsResult = useMemo(() => {
		if (showYearlyContributions.variation !== 'control') {
			return yearlyContributionsExperimentAmounts({
				yearlyContributionsExpVariation: showYearlyContributions.variation,
				yearlyContributionsAmounts,
				paymentTerm,
				buttonAmounts,
				currency,
			});
		}
		return buttonAmounts;
	}, [
		buttonAmounts,
		currency,
		paymentTerm,
		showYearlyContributions.variation,
		yearlyContributionsAmounts,
		yearlyContributionsExperimentAmounts,
	]);

	const buttonAmountOptions = useMemo(() => {
		if (iosAmountExperimentResult.enabled) {
			return buttonAmounts.map((amountPair) => {
				return getSubscriptionAmount(
					{
						amount: Math.ceil(amountPair.amount * iosAmountExperimentResult.ratio),
						currency,
						currencyDisplay: 'narrowSymbol',
						amountInBaseUnits: false,
					},
					utilityContext,
				);
			});
		}
		return yearlyContributionsExperimentAmountsResult;
	}, [iosAmountExperimentResult, yearlyContributionsExperimentAmountsResult, buttonAmounts, currency, utilityContext]);

	const selectedAmount = useMemo(() => {
		if (showYearlyContributions.variation !== 'control') {
			return yearlyContributionsExperimentAmountsResult[1];
		}
		return defaultAmountExperiment.variation === 'variant' || iosAmountExperimentResult.enabled
			? buttonAmountOptions[2]
			: defaultAmount;
	}, [
		showYearlyContributions.variation,
		defaultAmountExperiment.variation,
		iosAmountExperimentResult.enabled,
		buttonAmountOptions,
		defaultAmount,
		yearlyContributionsExperimentAmountsResult,
	]);

	const onClick = useCallback(
		(amount?: number) => {
			void track(`${sourceLocation}_membership_ask_click`);
			if (subscriptionConfig?.thirdPartySubscriptionService) {
				void track('membership_ask_click_third_party_subscription_service');
				navigate(subscriptionConfig?.thirdPartySubscriptionService);
				return;
			}
			const path = slug ? `/p/${encodeURIComponent(slug)}/psf/membership` : '/member/join';
			navigate(
				`${path}?source_location=${sourceLocation}&amount=${amountToBaseUnits(amount ?? selectedAmount.amount, currency)}&payment_term=${paymentTerm}`,
			);
		},
		[
			track,
			sourceLocation,
			subscriptionConfig?.thirdPartySubscriptionService,
			slug,
			navigate,
			amountToBaseUnits,
			selectedAmount.amount,
			currency,
			paymentTerm,
		],
	);

	return {
		data: {
			selectedAmount,
			buttonAmounts: buttonAmountOptions,
			buttonText: translate('corgi.membership.awareness.cta'),
			displayPayWithText: !subscriptionConfig?.thirdPartySubscriptionService,
		},
		actions: {
			onClick,
		},
	};
}
